import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Row, Col, Spin, Select, Tag, Modal, Progress, message } from "antd";
import {
  IdcardOutlined, CreditCardOutlined, ProfileOutlined, AuditOutlined, FieldTimeOutlined,
  CheckOutlined, ExclamationOutlined, UserAddOutlined, SafetyCertificateOutlined, ArrowLeftOutlined,
  ArrowRightOutlined, ReloadOutlined, CheckCircleFilled, CloseCircleFilled, WarningFilled,
} from "@ant-design/icons";
import { Form, FormItem, InputItem, SelectItem, SwitchItem, ButtonItem, ErrorItem } from "../../../../Components/Items";
import EditarEmpleado from "../EditarEmpleado/EditarEmpleado";
import InfonvitEmpleado from "../../PreNomina/InfonvitEmpleado/InfonvitEmpleado";
import FonacotEmpleado from "../../PreNomina/FonacotEmpleado/FonacotEmpleado";
import PasoRelojChecador from "./PasoRelojChecador";
import CalculadoraCurp from "../CalculadoraCurp";
import CalculadoraRfc from "../CalculadoraRfc";
import { momentDeFechaDia, fechaDiaParaPayload } from "../../../../Components/fmtFechaDia";
import "../capturaEmpleado.css";
import "./AltaEmpleadoPorPasos.css";

// Alta de empleado por pasos (complemento `altaEmpleadoPorPasos`). Se abre con
// history.push("/panel/altaEmpleadoPorPasos", { id }) para retomar una ficha incompleta, o sin
// id para empezar una nueva.
//
// Desde que se validan los datos generales el empleado YA existe, como ficha incompleta
// (estado 3): por eso el paso de créditos puede usar las pantallas de siempre y el de demás datos
// es Editar Empleado embebido, recorrido sección por sección. Quien decide si se puede dar de
// alta es el backend (Controllers/Empleados/altaPorPasos.controllers.js), que vuelve a revisar
// todo con lo guardado. Ya activo, el último paso ofrece mandarlo a un reloj checador.

const { Option } = Select;
const LBL = { span: 8 };
const WRP = { span: 16 };

const PASO = { GENERALES: 0, CREDITOS: 1, DEMAS: 2, REVISION: 3, RELOJ: 4 };
const PASOS = [
  { titulo: "Datos generales", descripcion: "Identidad y datos fiscales del empleado. Se cotejan contra el SAT antes de seguir.", icono: <IdcardOutlined /> },
  { titulo: "Créditos", descripcion: "Si el empleado tiene crédito INFONAVIT o FONACOT, se captura aquí.", icono: <CreditCardOutlined /> },
  { titulo: "Demás datos", descripcion: "", icono: <ProfileOutlined /> },
  { titulo: "Revisión general", descripcion: "Se revisa todo antes de darlo de alta, incluida la validación de timbrado.", icono: <AuditOutlined /> },
  { titulo: "Reloj checador", descripcion: "", icono: <FieldTimeOutlined /> },
];
const PASOS_OBLIGATORIOS = 4;

// Secciones del paso "Demás datos". Cada una le dice a Editar Empleado (embebido) qué mostrar:
// el bloque de encabezado y/o UNA de sus pestañas (las claves son las de sus TabPane).
// "Puesto y jornada" va primero a propósito: ahí está el tipo de prestación, que el SBC automático
// de "Contrato y sueldo" necesita, y el tipo de régimen, que decide si se piden NSS y SBC.
//
// `requeridos`: los obligatorios de nómina de cada sección (lo que la validación de timbrado y la
// prenómina necesitan). Se exigen los de la sección actual y las anteriores, para que guardar una
// sección no se trabe por un campo de otra que todavía no se visita. NSS, registro patronal y SBC
// además sólo aplican si cotiza en el IMSS (lo decide Editar Empleado).
//
// Sin sección de Conceptos a propósito (pedido del usuario): la ficha nace con los conceptos
// automáticos globales de la empresa (altaPorPasosDatosGenerales), que son los mismos que la
// pestaña muestra por omisión. La pestaña sigue montada aunque no se vea, así que `conceptosIds`
// viaja sin cambios en cada guardado; si el régimen pasa a asimilados, Editar Empleado propone el
// cambio de conceptos con su propio aviso, igual que siempre.
const SECCIONES_DEMAS = [
  { clave: "puesto", titulo: "Puesto y jornada", descripcion: "Tipo de prestación, jornada, régimen, departamento, puesto y forma de pago.", tab: "1",
    requeridos: ["tipoPrestacionId", "tipoJornadaId", "tipoRegimenId", "regimenFiscalId"] },
  { clave: "contrato", titulo: "Contrato y sueldo", descripcion: "Fecha de alta, contrato, periodo y salario. El SBC se calcula solo al capturar el salario.", encabezado: true, tab: "5",
    requeridos: ["fechaAlta", "tipoContratoId", "tipoPeriodoId", "salarioDiario", "numSeguridadSocial", "sbcParteFija"] },
  { clave: "domicilio", titulo: "Domicilio", descripcion: "Calle, número y colonia del domicilio.", tab: "2",
    requeridos: ["coloniaId"] },
  { clave: "imss", titulo: "IMSS y datos personales", descripcion: "Registro patronal, UMF, nacimiento y estado civil.", tab: "4",
    requeridos: ["registroPatronalId", "numSeguridadSocial"] },
  { clave: "pagos", titulo: "Pagos y extras", descripcion: "Banco, cuenta y CLABE para el pago electrónico.", tab: "6" },
  { clave: "contactos", titulo: "Contactos", descripcion: "Teléfonos y contactos de emergencia.", tab: "11" },
  { clave: "documentos", titulo: "Documentos", descripcion: "Contrato, identificaciones y otros archivos.", tab: "7" },
];
const camposRequeridosHasta = (indice) =>
  [...new Set(SECCIONES_DEMAS.slice(0, indice + 1).flatMap((s) => s.requeridos || []))];

// A qué sección de "Demás datos" lleva un campo con error (validación del formulario o de la
// revisión general). Los de identidad se capturan en el paso 1.
const CAMPOS_GENERALES = ["codigo", "nombre", "apellidoPaterno", "apellidoMaterno", "rfc", "curp", "extranjeroSinCurp", "correoElectronico", "coloniaId", "sat", "empresa"];
const SECCION_DE_CAMPO = {
  fechaAlta: "contrato", fechaReingreso: "contrato", tipoContratoId: "contrato", tipoPeriodoId: "contrato",
  salarioDiario: "contrato", baseDeCotizacion: "contrato", numSeguridadSocial: "contrato",
  sbcParteFija: "contrato", sbcParteVariable: "contrato", sbcTopado25UMAS: "contrato",
  tipoPrestacionId: "puesto", tipoJornadaId: "puesto", tipoRegimenId: "puesto", regimenFiscalId: "puesto", departamentoId: "puesto",
  coloniaId: "domicilio",
  puestoId: "puesto", registroIDTributario: "puesto", paisResidenciaId: "puesto",
  figuraTransporteId: "puesto", numLicencia: "puesto", vigenciaLicencia: "puesto",
  registroPatronalId: "imss",
  clabeInterbancaria: "pagos", bancoPagoElectronicoId: "pagos",
  telefonos: "contactos", contactosEmergencia: "contactos",
  documentos: "documentos",
};
const indiceSeccion = (clave) => Math.max(SECCIONES_DEMAS.findIndex((s) => s.clave === clave), 0);

const soloDigitos = (e) => String(e?.target?.value ?? "").replace(/[^0-9]/g, "");
const mayusculas = (e) => String(e?.target?.value ?? "").toUpperCase();
const nombreDe = (v) => [v?.nombre, v?.apellidoPaterno, v?.apellidoMaterno].filter(Boolean).join(" ");

const confirmar = (opciones) => new Promise((resolve) => {
  Modal.confirm({ ...opciones, onOk: () => resolve(true), onCancel: () => resolve(false) });
});

const Lista = ({ titulo, items }) => (items && items.length ? (
  <>
    {titulo && <div className="altapasos-lista-titulo">{titulo}</div>}
    <ul className="altapasos-lista">
      {items.map((m, i) => (
        <li key={i}>{m.codigo ? <b>{m.codigo}: </b> : null}{m.mensaje}</li>
      ))}
    </ul>
  </>
) : null);

class AltaEmpleadoPorPasos extends Component {
  formRef = React.createRef();
  // Lo que expone Editar Empleado embebido mientras está montado ({ guardar }).
  accionesDemas = null;

  state = {
    loading: false,
    id: this.props.location?.state?.id || null,
    nombreEmpleado: "",
    paso: PASO.GENERALES,
    // Hasta dónde se puede navegar: el primer paso pendiente que reporta el backend.
    pasoMaximo: PASO.GENERALES,
    completado: false,
    regimenesFiscales: [],
    colonias: [],
    ubicacionCp: {},
    resultadoGenerales: null,
    ultimaValidacion: null,
    tieneInfonavit: null,
    tieneFonacot: null,
    erroresCreditos: [],
    seccion: 0,
    seccionesGuardadas: {},
    guardandoDemas: false,
    revision: null,
    cargandoRevision: false,
    // Sexo, fecha y estado de nacimiento: el paso 1 no tiene esos campos, pero las calculadoras de
    // CURP y RFC los piden. Lo que se capture ahí viaja con los datos generales (sólo si se usó una
    // calculadora, para no pisar lo que ya se haya capturado en "IMSS y datos personales").
    nacimiento: {},
    nacimientoPorGuardar: false,
    // Fecha de alta con que quedó activo: es el inicio propuesto de su asignación de turno.
    fechaAltaEmpleado: null,
    // Ya se mandó a un reloj y falta asignarle turno: no se deja terminar (PasoRelojChecador).
    turnoPendiente: false,
  };

  componentDidMount() {
    this.cargarDatos();
  }

  headers() {
    return { headers: this.props.headersToken };
  }

  // `soloFormulario`: recarga los datos generales guardados sin mover el paso. Se usa al regresar
  // al paso 1 desde "Demás datos": ahí se pueden haber cambiado el RFC, el nombre o el domicilio,
  // y volver a validar con lo que se cargó al abrir los sobreescribiría con los valores viejos.
  async cargarDatos({ soloFormulario = false } = {}) {
    this.setState({ loading: true });
    try {
      const res = await axios.post("empleados/datosAltaPorPasos", {
        empresaId: this.props.empresaId,
        empleadoId: this.state.id || undefined,
      }, this.headers());
      if (res.data.success !== 2) {
        message.error(res.data.message || "No se pudo abrir el alta");
        if (this.state.id) this.props.history.goBack();
        return;
      }

      const regimenesFiscales = res.data.regimenesFiscales || [];
      const emp = res.data.empleado;
      if (!emp) {
        const r605 = regimenesFiscales.find((r) => String(r.c_RegimenFiscal) === "605");
        this.formRef.current?.setFieldsValue({
          codigo: res.data.siguienteCodigo,
          regimenFiscalId: r605?._id,
          extranjeroSinCurp: false,
        });
        this.setState({ regimenesFiscales });
        return;
      }

      this.formRef.current?.setFieldsValue({
        codigo: emp.codigo,
        nombre: emp.nombre,
        apellidoPaterno: emp.apellidoPaterno,
        apellidoMaterno: emp.apellidoMaterno,
        rfc: emp.rfc,
        curp: emp.curp,
        extranjeroSinCurp: !!emp.extranjeroSinCurp,
        correoElectronico: emp.correoElectronico,
        coloniaId: emp.coloniaId,
        regimenFiscalId: emp.regimenFiscalId,
      });
      const ubicacion = {
        regimenesFiscales,
        nombreEmpleado: nombreDe(emp),
        colonias: res.data.colonia ? [res.data.colonia] : [],
        ubicacionCp: { paisId: emp.paisId, estadoId: emp.estadoId, municipioId: emp.municipioId },
        nacimiento: {
          sexo: emp.sexo,
          fechaNacimiento: momentDeFechaDia(emp.fechaNacimiento),
          estadoNacimientoId: emp.estadoNacimientoId,
        },
        nacimientoPorGuardar: false,
      };
      if (soloFormulario) {
        this.setState(ubicacion);
        return;
      }

      const alta = emp.altaPorPasos || {};
      const pasoMaximo = Math.min(Math.max(Number(alta.paso) || 0, 0), PASO.REVISION);
      this.setState({
        ...ubicacion,
        tieneInfonavit: typeof alta.tieneInfonavit === "boolean" ? alta.tieneInfonavit : null,
        tieneFonacot: typeof alta.tieneFonacot === "boolean" ? alta.tieneFonacot : null,
        // Lo que encontró la última validación, para que al retomar se vea por qué se quedó aquí.
        ultimaValidacion: alta.datosGeneralesOk === false && (alta.hallazgosSat?.length || alta.errorSat)
          ? { hallazgos: alta.hallazgosSat || [], error: alta.errorSat || null }
          : null,
        pasoMaximo,
        paso: pasoMaximo,
      }, () => { if (pasoMaximo === PASO.REVISION) this.cargarRevision(); });
    } catch (err) {
      console.log(err);
      message.error("No se pudo abrir el alta");
    } finally {
      this.setState({ loading: false });
    }
  }

  // ── Navegación ─────────────────────────────────────────────────────────────

  puedeIr(paso) {
    const { id, pasoMaximo, completado } = this.state;
    // Ya dado de alta no hay vuelta atrás: sólo queda el paso del reloj.
    if (completado) return paso === PASO.RELOJ;
    if (paso === PASO.RELOJ) return false;
    if (paso === PASO.GENERALES) return true;
    if (!id) return false;
    // La revisión se puede pedir en cuanto se llega a los demás datos: sólo revisa.
    if (paso === PASO.REVISION) return pasoMaximo >= PASO.DEMAS;
    return paso <= pasoMaximo;
  }

  // Salir de "Demás datos" guarda primero: Editar Empleado se desmonta y lo capturado se perdería.
  // Si no se puede guardar (campos por corregir), se pregunta antes de perderlo.
  async soltarDemasDatos() {
    if (this.state.paso !== PASO.DEMAS || !this.accionesDemas) return true;
    if (await this.guardarDemas()) return true;
    return confirmar({
      title: "No se pudieron guardar los demás datos",
      content: "Hay campos por corregir en esta sección. ¿Salir sin guardar los cambios?",
      okText: "Salir sin guardar",
      cancelText: "Quedarme",
    });
  }

  ir = async (paso, { seccion } = {}) => {
    if (!this.puedeIr(paso)) {
      message.info(this.state.completado
        ? "El empleado ya está dado de alta"
        : this.state.id ? "Termina primero el paso anterior" : "Primero valida los datos generales");
      return;
    }
    if (paso === this.state.paso && paso === PASO.DEMAS) {
      if (seccion !== undefined) this.irASeccion(seccion);
      return;
    }
    if (!(await this.soltarDemasDatos())) return;
    this.navegar(paso, seccion);
  };

  navegar(paso, seccion) {
    const venia = this.state.paso;
    if (venia === PASO.DEMAS && paso !== PASO.DEMAS) this.accionesDemas = null;
    this.setState((s) => ({ paso, seccion: seccion !== undefined ? seccion : s.seccion }), () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (paso === PASO.REVISION) this.cargarRevision();
      if (paso === PASO.GENERALES && venia >= PASO.DEMAS && this.state.id) this.cargarDatos({ soloFormulario: true });
    });
  }

  salir = async () => {
    if (this.state.completado && this.state.turnoPendiente) {
      message.info("Ya está en un reloj checador: asígnale su turno antes de terminar");
      return;
    }
    if (!this.state.completado && !(await this.soltarDemasDatos())) return;
    this.props.history.goBack();
  };

  // ── Paso 1: datos generales ────────────────────────────────────────────────

  // buscarCp devuelve UNA colonia del CP (o crea una pendiente si no existe); las demás del
  // mismo CP se sacan de las de su municipio. Es la misma pareja de llamadas que Agregar Empleado.
  buscarCp = async (valor) => {
    const cp = String(valor || "").trim();
    if (!/^\d{5}$/.test(cp)) return;
    try {
      const res = await axios.post("colonias/buscarCp", { cp }, this.headers());
      if (res.data.status === 404 || !res.data._id) {
        message.info("No se encontró el código postal");
        return;
      }
      let colonias = [res.data];
      if (res.data.municipioId) {
        const r2 = await axios.post("colonias/selectColonias", { municipioId: res.data.municipioId }, this.headers());
        const delCp = (Array.isArray(r2.data) ? r2.data : []).filter((c) => String(c.cp) === cp);
        if (delCp.length) colonias = delCp;
      }
      this.setState({
        colonias,
        ubicacionCp: { paisId: res.data.paisId, estadoId: res.data.estadoId, municipioId: res.data.municipioId },
      });
      if (colonias.length === 1) this.formRef.current?.setFieldsValue({ coloniaId: colonias[0]._id });
    } catch (err) {
      console.log(err);
      message.error("No se pudo buscar el código postal");
    }
  };

  // Botón de tipo "button" + validateFields, no submit: el Form de la casa ignora un segundo
  // onFinish durante 5 s, y lo normal aquí es corregir lo que dijo el SAT y volver a validar.
  validarDatosGenerales = async () => {
    let values;
    try {
      values = await this.formRef.current.validateFields();
    } catch (e) {
      ErrorItem(e);
      return;
    }

    const { nacimiento, nacimientoPorGuardar } = this.state;
    this.setState({ loading: true, resultadoGenerales: null, ultimaValidacion: null });
    try {
      const res = await axios.post("empleados/altaPorPasosDatosGenerales", {
        ...values,
        ...(nacimientoPorGuardar ? {
          sexo: nacimiento.sexo,
          fechaNacimiento: fechaDiaParaPayload(nacimiento.fechaNacimiento),
          estadoNacimientoId: nacimiento.estadoNacimientoId,
        } : {}),
        ...this.state.ubicacionCp,
        empresaId: this.props.empresaId,
        empleadoId: this.state.id || undefined,
      }, this.headers());

      if (res.data.success !== 2) {
        message.error(res.data.message || "No se pudieron guardar los datos generales");
        return;
      }

      const id = res.data.id;
      // La ficha ya existe: se deja en el state del router para que recargar o regresar no la pierda.
      if (!this.state.id) this.props.history.replace(this.props.location.pathname, { id });

      const comun = { id, resultadoGenerales: res.data, nombreEmpleado: nombreDe(values), nacimientoPorGuardar: false };
      if (res.data.ok) {
        message.success("Datos generales validados en el SAT");
        this.setState({ ...comun, pasoMaximo: Math.max(this.state.pasoMaximo, PASO.CREDITOS) }, () => this.ir(PASO.CREDITOS));
      } else {
        this.setState({ ...comun, pasoMaximo: PASO.GENERALES });
      }
    } catch (err) {
      console.log(err);
      message.error("No se pudieron guardar los datos generales");
    } finally {
      this.setState({ loading: false });
    }
  };

  renderResultadoGenerales() {
    const { resultadoGenerales: r, ultimaValidacion } = this.state;
    if (!r) {
      if (!ultimaValidacion) return null;
      return (
        <div className="altapasos-panel aviso">
          <div className="altapasos-panel-titulo"><WarningFilled style={{ color: "#faad14" }} /> La última validación no pasó</div>
          <Lista titulo="El SAT respondió" items={ultimaValidacion.hallazgos} />
          {ultimaValidacion.error && <div className="altapasos-panel-texto">No se pudo consultar el SAT: {ultimaValidacion.error}</div>}
          <div className="altapasos-panel-texto">Corrige y vuelve a validar.</div>
        </div>
      );
    }
    if (r.ok) {
      return (
        <div className="altapasos-panel ok">
          <div className="altapasos-panel-titulo"><CheckCircleFilled style={{ color: "#2e9e6b" }} /> El SAT dio su visto bueno</div>
          <div className="altapasos-checks">
            {["RFC registrado", "Nombre coincide", "Código postal", "Régimen fiscal"].map((t) => (
              <span key={t}><CheckOutlined /> {t}</span>
            ))}
          </div>
          <Lista titulo="Avisos" items={r.advertencias} />
        </div>
      );
    }
    return (
      <>
        <div className="altapasos-panel error">
          <div className="altapasos-panel-titulo"><CloseCircleFilled style={{ color: "#ff4d4f" }} /> Todavía no se puede avanzar</div>
          <div className="altapasos-panel-texto">
            La ficha ya quedó guardada como incompleta. Corrige lo que se indica y vuelve a validar, o sal y
            retómala después desde el listado de Empleados.
          </div>
          <Lista titulo="Datos por corregir" items={r.errores} />
          <Lista titulo="El SAT respondió" items={r.sat?.hallazgos} />
        </div>
        {r.sat?.error && (
          <div className="altapasos-panel aviso">
            <div className="altapasos-panel-titulo"><WarningFilled style={{ color: "#faad14" }} /> {r.sat.error}</div>
            <div className="altapasos-panel-texto">Sin la respuesta del SAT no se puede avanzar. Inténtalo de nuevo en unos minutos.</div>
          </div>
        )}
        {r.advertencias?.length > 0 && (
          <div className="altapasos-panel aviso"><Lista titulo="Avisos" items={r.advertencias} /></div>
        )}
      </>
    );
  }

  renderDatosGenerales() {
    return (
      <Form ref={this.formRef} name="altaEmpleadoDatosGenerales" layout>
        <Row gutter={32}>
          <Col xs={24} lg={12}>
            <div className="altapasos-subtitulo">Identidad</div>
            <FormItem label="Código" name="codigo" margin required labelCol={LBL} wrapperCol={WRP} getValueFromEvent={soloDigitos}>
              <InputItem placeholder="Código empleado" inputMode="numeric" />
            </FormItem>
            <FormItem label="Nombre(s)" name="nombre" margin pattern required labelCol={LBL} wrapperCol={WRP}>
              <InputItem placeholder="Nombre(s)" />
            </FormItem>
            <FormItem label="Ap. Paterno" name="apellidoPaterno" margin pattern required labelCol={LBL} wrapperCol={WRP}>
              <InputItem placeholder="Apellido Paterno" />
            </FormItem>
            <FormItem label="Ap. Materno" name="apellidoMaterno" margin pattern labelCol={LBL} wrapperCol={WRP}>
              <InputItem placeholder="Apellido Materno" />
            </FormItem>
            <FormItem name="extranjeroSinCurp" label="Sin CURP" margin initialValue={false} valuePropName="checked" labelCol={LBL} wrapperCol={WRP}
              tooltip="Sólo para un extranjero que no tiene CURP">
              <SwitchItem />
            </FormItem>
            <FormItem noStyle shouldUpdate={(antes, ahora) => antes.extranjeroSinCurp !== ahora.extranjeroSinCurp}>
              {({ getFieldValue }) => (
                <FormItem label="CURP" name="curp" margin curp required={!getFieldValue("extranjeroSinCurp")} labelCol={LBL} wrapperCol={WRP} getValueFromEvent={mayusculas}>
                  <InputItem placeholder="CURP" suffix={
                    <CalculadoraCurp
                      obtenerForm={() => this.formRef.current}
                      datosExtra={this.state.nacimiento}
                      nacimientoEnFormulario={false}
                      onUsar={({ sexo, fechaNacimiento, estadoNacimientoId }) => this.setState((s) => ({
                        nacimiento: { sexo, fechaNacimiento, estadoNacimientoId: estadoNacimientoId || s.nacimiento.estadoNacimientoId },
                        nacimientoPorGuardar: true,
                      }))}
                    />
                  } />
                </FormItem>
              )}
            </FormItem>
          </Col>
          <Col xs={24} lg={12}>
            <div className="altapasos-subtitulo">Datos fiscales</div>
            <FormItem label="RFC" name="rfc" margin rfc required labelCol={LBL} wrapperCol={WRP} getValueFromEvent={mayusculas}>
              <InputItem placeholder="RFC (13 caracteres)" suffix={
                <CalculadoraRfc
                  obtenerForm={() => this.formRef.current}
                  datosExtra={this.state.nacimiento}
                  nacimientoEnFormulario={false}
                  onUsar={({ fechaNacimiento }) => this.setState((s) => ({
                    nacimiento: { ...s.nacimiento, fechaNacimiento },
                    nacimientoPorGuardar: true,
                  }))}
                />
              } />
            </FormItem>
            <FormItem label="Código Postal" name="coloniaId" margin required labelCol={LBL} wrapperCol={WRP}
              tooltip="El del domicilio fiscal del empleado (el de su constancia de situación fiscal). Escribe los 5 dígitos y elige la colonia.">
              <SelectItem placeholder="Escribe el código postal" onSearch={this.buscarCp}>
                {this.state.colonias.map((c) => (
                  <Option key={c._id} value={c._id}>{`${c.cp} - ${c.nombre}`}</Option>
                ))}
              </SelectItem>
            </FormItem>
            <FormItem label="Régimen Fiscal" name="regimenFiscalId" margin required labelCol={LBL} wrapperCol={WRP}>
              <SelectItem placeholder="Régimen fiscal">
                {this.state.regimenesFiscales.map((r) => (
                  <Option key={r._id} value={r._id}>{`${r.c_RegimenFiscal} - ${r.descripcion}`}</Option>
                ))}
              </SelectItem>
            </FormItem>
            <FormItem label="Correo" name="correoElectronico" margin labelCol={LBL} wrapperCol={WRP}>
              <InputItem placeholder="correo@empresa.com" />
            </FormItem>
          </Col>
        </Row>
        {this.renderResultadoGenerales()}
      </Form>
    );
  }

  // ── Paso 2: créditos ───────────────────────────────────────────────────────

  guardarCreditos = async () => {
    const { id, tieneInfonavit, tieneFonacot } = this.state;
    this.setState({ loading: true, erroresCreditos: [] });
    try {
      const res = await axios.post("empleados/altaPorPasosCreditos", {
        empresaId: this.props.empresaId, empleadoId: id, tieneInfonavit, tieneFonacot,
      }, this.headers());
      if (res.data.success !== 2) {
        message.error(res.data.message || "No se pudieron guardar las respuestas");
        return;
      }
      if (res.data.ok) {
        this.setState({ pasoMaximo: Math.max(this.state.pasoMaximo, PASO.DEMAS) }, () => this.ir(PASO.DEMAS, { seccion: 0 }));
      } else {
        this.setState({ erroresCreditos: res.data.errores || [], pasoMaximo: Math.min(this.state.pasoMaximo, PASO.CREDITOS) });
      }
    } catch (err) {
      console.log(err);
      message.error("No se pudieron guardar las respuestas");
    } finally {
      this.setState({ loading: false });
    }
  };

  renderOpcion(campo, valor, texto) {
    const elegida = this.state[campo] === valor;
    return (
      <div
        className={`altapasos-opcion${elegida ? " elegida" : ""}`}
        role="button"
        tabIndex={0}
        onClick={() => this.setState({ [campo]: valor, erroresCreditos: [] })}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); this.setState({ [campo]: valor, erroresCreditos: [] }); } }}
      >
        <span className="altapasos-opcion-marca"><CheckOutlined /></span>
        {texto}
      </div>
    );
  }

  renderPregunta(titulo, ayuda, campo, contenido) {
    return (
      <div className="altapasos-pregunta">
        <div className="altapasos-pregunta-titulo">{titulo}</div>
        <div className="altapasos-pregunta-ayuda">{ayuda}</div>
        <div className="altapasos-opciones">
          {this.renderOpcion(campo, true, "Sí, tiene")}
          {this.renderOpcion(campo, false, "No tiene")}
        </div>
        {this.state[campo] === true && <div className="altapasos-dependiente">{contenido}</div>}
      </div>
    );
  }

  renderCreditos() {
    const { id, erroresCreditos } = this.state;
    if (!id) return null;
    const limpiar = () => this.setState({ erroresCreditos: [] });
    return (
      <>
        {this.renderPregunta(
          "¿El empleado tiene crédito INFONAVIT?",
          "Si contestas que sí, captura el crédito aquí mismo: número, tipo de descuento y fecha de aplicación.",
          "tieneInfonavit",
          <InfonvitEmpleado empleadoId={id} onCambio={limpiar} />
        )}
        {this.renderPregunta(
          "¿El empleado tiene crédito FONACOT?",
          "Si contestas que sí, captura el crédito con su fecha de aplicación: sin ella nunca se descuenta.",
          "tieneFonacot",
          <FonacotEmpleado empleadoId={id} onCambio={limpiar} />
        )}
        {erroresCreditos.length > 0 && (
          <div className="altapasos-panel error">
            <div className="altapasos-panel-titulo"><CloseCircleFilled style={{ color: "#ff4d4f" }} /> Todavía no se puede avanzar</div>
            <Lista items={erroresCreditos} />
          </div>
        )}
      </>
    );
  }

  // ── Paso 3: demás datos (Editar Empleado embebido, una sección a la vez) ──

  // Guarda TODO el formulario de Editar Empleado. Se llama en cada "Siguiente" para que lo
  // capturado no dependa de llegar a la última sección.
  async guardarDemas() {
    if (!this.accionesDemas) return true;
    this.setState({ guardandoDemas: true });
    try {
      const ok = await this.accionesDemas.guardar();
      if (ok) {
        const clave = SECCIONES_DEMAS[this.state.seccion].clave;
        this.setState((s) => ({ seccionesGuardadas: { ...s.seccionesGuardadas, [clave]: true } }));
      }
      return ok;
    } finally {
      this.setState({ guardandoDemas: false });
    }
  }

  irASeccion = (seccion) => {
    this.setState({ seccion }, () => window.scrollTo({ top: 0, behavior: "smooth" }));
  };

  siguienteSeccion = async () => {
    if (!(await this.guardarDemas())) return;
    const { seccion } = this.state;
    if (seccion < SECCIONES_DEMAS.length - 1) {
      message.success("Sección guardada", 1);
      this.irASeccion(seccion + 1);
      return;
    }
    // Última sección: ya está guardada, se pasa directo a la revisión.
    this.setState((s) => ({ pasoMaximo: Math.max(s.pasoMaximo, PASO.REVISION) }), () => this.navegar(PASO.REVISION));
  };

  anteriorSeccion = () => {
    if (this.state.seccion > 0) this.irASeccion(this.state.seccion - 1);
    else this.ir(PASO.CREDITOS);
  };

  // Error de validación de Editar Empleado: se abre la sección donde está el campo.
  onCampoConError = (campo) => {
    // Primero una sección de aquí mismo (el código postal también está en Domicilio).
    if (SECCION_DE_CAMPO[campo]) {
      this.irASeccion(indiceSeccion(SECCION_DE_CAMPO[campo]));
      return;
    }
    if (CAMPOS_GENERALES.includes(campo)) message.info("Ese dato se corrige en el paso Datos generales");
  };

  // ── Paso 4: revisión general ───────────────────────────────────────────────

  cargarRevision = async () => {
    if (!this.state.id) return;
    this.setState({ cargandoRevision: true });
    try {
      const res = await axios.post("empleados/altaPorPasosRevision", {
        empresaId: this.props.empresaId, empleadoId: this.state.id,
      }, this.headers());
      if (res.data.success !== 2) {
        message.error(res.data.message || "No se pudo hacer la revisión general");
        return;
      }
      this.setState({ revision: res.data });
    } catch (err) {
      console.log(err);
      message.error("No se pudo hacer la revisión general");
    } finally {
      this.setState({ cargandoRevision: false });
    }
  };

  darDeAlta = async () => {
    this.setState({ loading: true });
    try {
      const res = await axios.post("empleados/altaPorPasosCompletar", {
        empresaId: this.props.empresaId, empleadoId: this.state.id,
      }, this.headers());
      if (res.data.success === 2) {
        message.success("Empleado dado de alta");
        this.setState((s) => ({
          completado: true,
          pasoMaximo: PASO.RELOJ,
          fechaAltaEmpleado: res.data.empleado?.fechaAlta || s.revision?.empleado?.fechaAlta || null,
        }), () => this.navegar(PASO.RELOJ));
        return;
      }
      message.error(res.data.message || "No se pudo dar de alta al empleado");
      if (res.data.revision) this.setState({ revision: { success: 2, ...res.data.revision } });
    } catch (err) {
      console.log(err);
      message.error("No se pudo dar de alta al empleado");
    } finally {
      this.setState({ loading: false });
    }
  };

  // A dónde lleva el "Ir" de un pendiente de la revisión. `paso` null = no viene de un paso (los
  // duplicados): se decide sólo por el campo.
  irAError = (paso, campo) => {
    // Un pendiente de "Demás datos" se queda ahí aunque sea un hallazgo del SAT (campo "sat").
    if (paso === PASO.DEMAS) return this.ir(PASO.DEMAS, { seccion: indiceSeccion(SECCION_DE_CAMPO[campo]) });
    if (paso === PASO.GENERALES || CAMPOS_GENERALES.includes(campo)) return this.ir(PASO.GENERALES);
    if (SECCION_DE_CAMPO[campo]) return this.ir(PASO.DEMAS, { seccion: indiceSeccion(SECCION_DE_CAMPO[campo]) });
    return this.ir(paso ?? PASO.GENERALES);
  };

  pendientesRevision() {
    const r = this.state.revision;
    if (!r) return null;
    return (r.duplicados?.length || 0)
      + (r.pasos || []).reduce((acc, p) => acc + (p.ok ? 0 : Math.max(p.errores?.length || 0, 1)), 0);
  }

  renderRevision() {
    const { revision } = this.state;
    if (!revision) return <div style={{ minHeight: 120 }} />;
    return (
      <>
        <div className={`altapasos-panel ${revision.listo ? "ok" : "aviso"}`} style={{ marginTop: 0, marginBottom: 16 }}>
          <div className="altapasos-panel-titulo">
            {revision.listo
              ? <><CheckCircleFilled style={{ color: "#2e9e6b" }} /> {revision.empleado?.nombre || "El empleado"} pasó todas las revisiones</>
              : <><WarningFilled style={{ color: "#faad14" }} /> Hay pendientes</>}
          </div>
          <div className="altapasos-panel-texto">
            {revision.listo
              ? "Ya se puede dar de alta. Al hacerlo deja de ser ficha incompleta y entra a las nóminas nuevas."
              : "El empleado sigue como ficha incompleta hasta corregirlos. Usa \"Ir\" para llegar a cada dato."}
          </div>
        </div>

        {revision.duplicados?.length > 0 && (
          <div className="altapasos-revision-fila">
            <div className="altapasos-revision-cabeza">
              <div className="altapasos-revision-icono pendiente"><ExclamationOutlined /></div>
              <div className="altapasos-revision-titulo">Datos que ya usa otro empleado activo</div>
              <span className="altapasos-pill pendiente">{revision.duplicados.length} pendiente(s)</span>
            </div>
            <div className="altapasos-revision-detalle">
              {revision.duplicados.map((e, i) => (
                <div key={i} className="altapasos-revision-error">
                  <span>{e.mensaje}</span>
                  <span className="altapasos-ir" onClick={() => this.irAError(null, e.campo)}>Ir</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(revision.pasos || []).map((p) => (
          <div key={p.clave} className="altapasos-revision-fila">
            <div className="altapasos-revision-cabeza">
              <div className={`altapasos-revision-icono ${p.ok ? "ok" : "pendiente"}`}>{PASOS[p.paso]?.icono}</div>
              <div className="altapasos-revision-titulo">{p.titulo}</div>
              <span className={`altapasos-pill ${p.ok ? "ok" : "pendiente"}`}>
                {p.ok ? "Listo" : `${Math.max(p.errores?.length || 0, 1)} pendiente(s)`}
              </span>
            </div>
            {(p.errores?.length > 0 || p.advertencias?.length > 0) && (
              <div className="altapasos-revision-detalle">
                {(p.errores || []).map((e, i) => (
                  <div key={`e${i}`} className="altapasos-revision-error">
                    <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 12 }} />
                    <span>{e.codigo ? <b>{e.codigo}: </b> : null}{e.mensaje}</span>
                    <span className="altapasos-ir" onClick={() => this.irAError(p.paso, e.campo)}>Ir</span>
                  </div>
                ))}
                {(p.advertencias || []).map((e, i) => (
                  <div key={`a${i}`} className="altapasos-revision-error aviso">
                    <WarningFilled style={{ color: "#faad14", fontSize: 12 }} />
                    <span>{e.mensaje} <i>(no impide el alta)</i></span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </>
    );
  }

  // ── Armado ─────────────────────────────────────────────────────────────────

  pasosVisibles() {
    // El del reloj sólo existe si la empresa usa control de asistencia.
    return this.props.controlAsistencia ? PASOS.length : PASOS_OBLIGATORIOS;
  }

  estadoPaso(i) {
    const { pasoMaximo, completado, revision } = this.state;
    if (i === PASO.RELOJ) return completado ? "hecho" : "";
    if (completado) return "hecho";
    // La revisión, cuando ya se hizo, manda: un paso que pasó y luego se rompió se marca.
    const enRevision = revision?.pasos?.find((p) => p.paso === i);
    if (enRevision && !enRevision.ok) return "pendiente";
    if (i === PASO.REVISION) return "";
    return i < pasoMaximo ? "hecho" : "";
  }

  lineaDeEstado(i) {
    const { id, pasoMaximo, completado, tieneInfonavit, tieneFonacot, seccionesGuardadas } = this.state;
    const siNo = (v) => (v === true ? "Sí" : v === false ? "No" : "—");
    switch (i) {
      case PASO.GENERALES:
        return pasoMaximo > PASO.GENERALES || completado ? "Validado en el SAT" : id ? "Falta el visto bueno del SAT" : "Por capturar";
      case PASO.CREDITOS:
        return typeof tieneInfonavit === "boolean" || typeof tieneFonacot === "boolean"
          ? `INFONAVIT: ${siNo(tieneInfonavit)} · FONACOT: ${siNo(tieneFonacot)}`
          : "Por contestar";
      case PASO.DEMAS:
        return `${Object.keys(seccionesGuardadas).length} de ${SECCIONES_DEMAS.length} secciones guardadas`;
      case PASO.REVISION: {
        if (completado) return "Dado de alta";
        const n = this.pendientesRevision();
        return n === null ? "Antes de dar de alta" : n === 0 ? "Todo en orden" : `${n} pendiente(s)`;
      }
      default:
        return "Opcional";
    }
  }

  renderLateral() {
    const { paso, seccion, seccionesGuardadas, pasoMaximo, completado } = this.state;
    const visibles = this.pasosVisibles();
    const avance = completado ? PASOS_OBLIGATORIOS : Math.min(pasoMaximo, PASOS_OBLIGATORIOS - 1);
    return (
      <div className="altapasos-lateral altapasos-sombra">
        {PASOS.slice(0, visibles).map((p, i) => {
          const estado = this.estadoPaso(i);
          const clases = ["altapasos-paso", estado, i === paso ? "activo" : "", !this.puedeIr(i) && i !== paso ? "bloqueado" : "", i === visibles - 1 ? "ultimo" : ""];
          return (
            <React.Fragment key={p.titulo}>
              <div className={clases.filter(Boolean).join(" ")} role="button" tabIndex={0} onClick={() => i !== paso && this.ir(i)}>
                <div className="altapasos-paso-riel">
                  <div className="altapasos-paso-circulo">
                    {estado === "hecho" && i !== paso ? <CheckOutlined /> : estado === "pendiente" && i !== paso ? <ExclamationOutlined /> : i + 1}
                  </div>
                  <div className="altapasos-paso-linea" />
                </div>
                <div className="altapasos-paso-texto">
                  <div className="altapasos-paso-titulo">{p.titulo}</div>
                  <div className="altapasos-paso-estado">{this.lineaDeEstado(i)}</div>
                </div>
              </div>
              {i === PASO.DEMAS && paso === PASO.DEMAS && (
                <div className="altapasos-secciones">
                  {SECCIONES_DEMAS.map((s, j) => (
                    <div
                      key={s.clave}
                      role="button"
                      tabIndex={0}
                      className={`altapasos-seccion${j === seccion ? " activa" : ""}${seccionesGuardadas[s.clave] ? " guardada" : ""}`}
                      onClick={() => this.irASeccion(j)}
                    >
                      <span className="altapasos-seccion-punto" />
                      {s.titulo}
                    </div>
                  ))}
                </div>
              )}
            </React.Fragment>
          );
        })}
        <div className="altapasos-progreso">
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span>Avance</span>
            <span>{avance} de {PASOS_OBLIGATORIOS} pasos</span>
          </div>
          <Progress percent={Math.round((avance / PASOS_OBLIGATORIOS) * 100)} showInfo={false} size="small" strokeColor="#00BFD6" />
        </div>
      </div>
    );
  }

  renderCabezaTarjeta() {
    const { paso, seccion } = this.state;
    const total = this.pasosVisibles();
    const p = PASOS[paso];
    let titulo = p.titulo;
    let descripcion = p.descripcion;
    let etiqueta = `Paso ${paso + 1} de ${total}`;
    if (paso === PASO.DEMAS) {
      const s = SECCIONES_DEMAS[seccion];
      titulo = s.titulo;
      descripcion = s.descripcion;
      etiqueta = `${etiqueta} · Demás datos · Sección ${seccion + 1} de ${SECCIONES_DEMAS.length}`;
    }
    if (paso === PASO.RELOJ) {
      titulo = "¡Listo!";
      descripcion = this.props.controlAsistencia
        ? "El alta terminó. Si quieres, envíalo a un reloj checador; si lo haces, asígnale también su turno."
        : "El alta terminó.";
      etiqueta = this.props.controlAsistencia ? etiqueta : "Alta completada";
    }
    return (
      <div className="altapasos-tarjeta-cabeza">
        <div className="altapasos-tarjeta-icono">{p.icono}</div>
        <div>
          <div className="altapasos-tarjeta-etiqueta">{etiqueta}</div>
          <div className="altapasos-tarjeta-titulo">{titulo}</div>
          {descripcion && <div className="altapasos-tarjeta-desc">{descripcion}</div>}
        </div>
      </div>
    );
  }

  renderPie() {
    const { paso, seccion, tieneInfonavit, tieneFonacot, revision, cargandoRevision, guardandoDemas } = this.state;
    const anterior = (texto, onClick) => (
      <ButtonItem blanco htmlType="button" icon={<ArrowLeftOutlined />} onClick={onClick}>{texto}</ButtonItem>
    );
    switch (paso) {
      case PASO.GENERALES:
        return (
          <div className="altapasos-tarjeta-pie">
            <span className="altapasos-tarjeta-pie-centro">Al validar, la ficha se guarda como incompleta.</span>
            <div className="altapasos-tarjeta-pie-derecha">
              <ButtonItem type="primary" cyan htmlType="button" icon={<SafetyCertificateOutlined />} onClick={this.validarDatosGenerales}>
                Validar en el SAT y continuar
              </ButtonItem>
            </div>
          </div>
        );
      case PASO.CREDITOS:
        return (
          <div className="altapasos-tarjeta-pie">
            {anterior("Anterior", () => this.ir(PASO.GENERALES))}
            <div className="altapasos-tarjeta-pie-derecha">
              <ButtonItem type="primary" cyan htmlType="button" disabled={typeof tieneInfonavit !== "boolean" || typeof tieneFonacot !== "boolean"} onClick={this.guardarCreditos}>
                Guardar y continuar <ArrowRightOutlined />
              </ButtonItem>
            </div>
          </div>
        );
      case PASO.DEMAS: {
        const ultima = seccion === SECCIONES_DEMAS.length - 1;
        return (
          <div className="altapasos-tarjeta-pie">
            {anterior(seccion > 0 ? SECCIONES_DEMAS[seccion - 1].titulo : "Créditos", this.anteriorSeccion)}
            <span className="altapasos-tarjeta-pie-centro">Cada "Siguiente" guarda lo capturado.</span>
            <div className="altapasos-tarjeta-pie-derecha">
              <ButtonItem type="primary" cyan htmlType="button" disabled={guardandoDemas} onClick={this.siguienteSeccion}>
                {ultima ? "Guardar y revisar" : `Siguiente: ${SECCIONES_DEMAS[seccion + 1].titulo}`} <ArrowRightOutlined />
              </ButtonItem>
            </div>
          </div>
        );
      }
      case PASO.REVISION:
        return (
          <div className="altapasos-tarjeta-pie">
            {anterior("Demás datos", () => this.ir(PASO.DEMAS))}
            <div className="altapasos-tarjeta-pie-derecha">
              <ButtonItem blanco htmlType="button" icon={<ReloadOutlined />} disabled={cargandoRevision} onClick={this.cargarRevision}>
                Volver a revisar
              </ButtonItem>
              <ButtonItem type="primary" cyan htmlType="button" icon={<UserAddOutlined />} disabled={!revision?.listo || cargandoRevision} onClick={this.darDeAlta}>
                Dar de alta al empleado
              </ButtonItem>
            </div>
          </div>
        );
      default:
        return null;
    }
  }

  render() {
    const { paso, id, seccion, completado, nombreEmpleado, cargandoRevision, guardandoDemas } = this.state;
    if (!this.props.nomina) {
      return (
        <div className="altapasos-panel aviso">
          <div className="altapasos-panel-titulo"><WarningFilled style={{ color: "#faad14" }} /> El alta por pasos necesita el módulo de nómina.</div>
        </div>
      );
    }
    const s = SECCIONES_DEMAS[seccion];
    return (
      <>
        <div className="altapasos-encabezado altapasos-sombra">
          <div className="altapasos-encabezado-icono"><UserAddOutlined /></div>
          <div>
            <div className="altapasos-encabezado-titulo">
              Alta de empleado
              {completado
                ? <Tag color="green" style={{ margin: 0 }}>Dado de alta</Tag>
                : id ? <Tag color="orange" style={{ margin: 0 }}>Ficha incompleta</Tag> : null}
            </div>
            <div className="altapasos-encabezado-sub">
              {nombreEmpleado || "Captura los datos del nuevo empleado paso por paso."}
            </div>
          </div>
          <div className="altapasos-encabezado-acciones">
            <ButtonItem blanco htmlType="button" onClick={this.salir}>
              {completado ? "Terminar" : id ? "Salir (queda como ficha incompleta)" : "Cancelar"}
            </ButtonItem>
          </div>
        </div>

        <div className="altapasos-cuerpo">
          {this.renderLateral()}

          <div className="altapasos-sombra">
            {this.renderCabezaTarjeta()}
            <Spin spinning={this.state.loading || cargandoRevision || guardandoDemas}>
              <div className="altapasos-tarjeta-cuerpo">
                {/* Los pasos 1 y 2 se quedan montados (ocultos) para no perder lo capturado al ir y
                    venir; Editar Empleado sólo se monta en su paso y se guarda antes de salir. */}
                <div className="captura-empleado" style={{ display: paso === PASO.GENERALES ? "block" : "none" }}>{this.renderDatosGenerales()}</div>
                <div style={{ display: paso === PASO.CREDITOS ? "block" : "none" }}>{this.renderCreditos()}</div>
                {/* La ficha nace con lo más común de la empresa (altaPorPasos.controllers.js,
                    valoresComunesParaFicha): se avisa en las dos secciones donde caen esos datos. */}
                {paso === PASO.DEMAS && id && seccion <= 1 && (
                  <div className="altapasos-nota">
                    Para ir rápido, los datos de nómina vienen llenos con lo más común en tu empresa
                    (tipo de prestación, jornada, régimen, contrato, periodo y registro patronal) y la
                    fecha de alta es la de hoy. Revísalos y cambia lo que no aplique a este empleado.
                  </div>
                )}
                {paso === PASO.DEMAS && id && (
                  <div className="captura-empleado">
                    <EditarEmpleado
                      key={id}
                      embebido
                      empleadoId={id}
                      seccion={{ encabezado: !!s.encabezado, tab: s.tab || null }}
                      camposRequeridos={camposRequeridosHasta(seccion)}
                      history={this.props.history}
                      location={this.props.location}
                      onRegistrarAcciones={(acciones) => { this.accionesDemas = acciones; }}
                      onCampoConError={this.onCampoConError}
                    />
                  </div>
                )}
                {paso === PASO.REVISION && this.renderRevision()}
                {paso === PASO.RELOJ && (
                  <PasoRelojChecador
                    empleadoId={id}
                    nombre={nombreEmpleado}
                    puedeAsignar={!!this.props.controlAsistencia && !!this.props.permisoAgregarAsignacionReloj}
                    puedeAsignarTurno={!!this.props.controlAsistencia && !!this.props.permisoAgregarAsignacionTurno}
                    fechaAlta={this.state.fechaAltaEmpleado}
                    onTurnoPendiente={(turnoPendiente) => this.setState({ turnoPendiente })}
                    onTerminar={() => this.props.history.goBack()}
                  />
                )}
              </div>
            </Spin>
            {this.renderPie()}
          </div>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  nomina: state.empresa.nomina,
  controlAsistencia: state.empresa.controlAsistencia,
  // Mismo permiso que el botón "Agregar" de Asignación de Relojes (AsignacionRelojes.js).
  permisoAgregarAsignacionReloj:
    state.user.tipo === 0 ||
    state.user.permisos.find(
      (valueTodos) => valueTodos.permisoId.toString() === "69f020a37899dda241377ce5"
    )?.activado,
  // Mismo permiso que el botón "Agregar" de Asignación de Turnos (AsignacionTurnos.js).
  permisoAgregarAsignacionTurno:
    state.user.tipo === 0 ||
    state.user.permisos.find(
      (valueTodos) => valueTodos.permisoId.toString() === "69f021de7899dda241377cf4"
    )?.activado,
});

export default connect(mapStateToProps)(AltaEmpleadoPorPasos);
